import { useState } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  EmptyState,
  Skeleton,
  ToggleGroup,
  ToggleGroupItem,
  SelectNative,
  type ChartConfig,
} from '@proerp/ui';
import type { MonthlyRevenuePoint } from './dashboardMetrics';

const chartConfig = {
  euros: {
    label: 'Umsatz',
    color: 'var(--chart-1)',
  },
} satisfies ChartConfig;

const EUR_FORMAT = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const RANGE_OPTIONS = [
  { months: 12, label: '12 Monate' },
  { months: 6, label: '6 Monate' },
  { months: 3, label: '3 Monate' },
] as const;

/**
 * Fakturierter Netto-Umsatz je Monat als Area-Chart
 * (Muster: shadcn dashboard-01 ChartAreaInteractive).
 *
 * `points` sind die letzten zwölf Monate (ältester zuerst); der Zeitraum-Schalter
 * zeigt davon die letzten 12/6/3. Quelle ist immer der Server
 * (`toMonthlyRevenuePoints` — Dashboard-Kennzahlen bzw. Analyse-Cockpit, über
 * alle Belege). `error`/`notice` ersetzen den Chart durch eine Meldung statt
 * einer Null-Linie.
 */
export function RevenueChart({
  points,
  isLoading,
  error,
  notice,
}: {
  points: MonthlyRevenuePoint[];
  isLoading: boolean;
  error?: string | null;
  /** Kein Fehler, aber keine Zahlen (z. B. fehlendes Recht) — Text statt Chart. */
  notice?: string | null;
}) {
  const [months, setMonths] = useState(12);
  const series = points.slice(-months);

  return (
    <Card>
      <CardHeader className="relative">
        <CardTitle>Umsatz</CardTitle>
        <CardDescription>Fakturierter Netto-Umsatz je Monat</CardDescription>
        <div className="absolute right-4 top-4">
          <ToggleGroup
            type="single"
            value={String(months)}
            onValueChange={(value) => value && setMonths(Number(value))}
            variant="outline"
            className="hidden md:flex"
          >
            {RANGE_OPTIONS.map((option) => (
              <ToggleGroupItem
                key={option.months}
                value={String(option.months)}
                className="h-8 px-2.5"
              >
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <SelectNative
            className="w-36 md:hidden"
            fieldSize="sm"
            value={String(months)}
            onChange={(event) => setMonths(Number(event.target.value))}
            aria-label="Zeitraum wählen"
          >
            {RANGE_OPTIONS.map((option) => (
              <option key={option.months} value={option.months}>
                {option.label}
              </option>
            ))}
          </SelectNative>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {isLoading ? (
          <Skeleton className="h-[250px] w-full" />
        ) : error ? (
          <EmptyState icon={AlertTriangle} title="Umsatz konnte nicht geladen werden." description={error} />
        ) : notice ? (
          <EmptyState icon={Lock} title={notice} />
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
            <AreaChart data={series}>
              <defs>
                <linearGradient id="fillUmsatz" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-euros)" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="var(--color-euros)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={16}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    formatter={(value) => (
                      <div className="flex w-full items-center justify-between gap-4">
                        <span className="text-muted-foreground">Umsatz</span>
                        <span className="font-medium tabular-nums text-foreground">
                          {EUR_FORMAT.format(Number(value))}
                        </span>
                      </div>
                    )}
                  />
                }
              />
              <Area
                dataKey="euros"
                // monotone: Spline schwingt nicht über die Datenpunkte → keine Linie unter 0.
                type="monotone"
                fill="url(#fillUmsatz)"
                stroke="var(--color-euros)"
                strokeWidth={2}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
